import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import pluralize from 'pluralize';
import moment from 'moment-timezone';
import cn from '@/utils/cn';
import Text from '@/components/ui/Text';
import Icon from '@/components/ui/Icon';
import { IImage } from '@/components/admin/Images/interfaces';
import {
  ImageModelResponse,
  ImageResponse,
  useMergedVersions,
  usePreviousVersions,
  useRevertedVersions,
  useVersionImageModels,
} from '../MergeHistoryModal.utils';

interface MergedImageCardProps {
  addToReverted: () => any;
  image: ImageResponse;
  previousImageModel: ImageModelResponse;
  targetImage: IImage;
}

export const MergedImageCard = ({ addToReverted, image, previousImageModel, targetImage }: MergedImageCardProps) => {
  const { register, watch } = useFormContext();
  const {
    data: versionImageModels,
    isFetching: isFetchingVersionImageModels,
  } = useVersionImageModels({ image: targetImage });
  const {
    data: mergedVersions,
    isFetching: isFetchingMergedVersions,
  } = useMergedVersions({ image: targetImage });
  const {
    data: revertedVersions,
    isFetching: isFetchingRevertedVersions,
  } = useRevertedVersions({ image, imageModels: versionImageModels });
  const {
    data: previouslyUsed,
  } = usePreviousVersions({
    image,
    isEnabled: versionImageModels.length === 0 && !isFetchingVersionImageModels,
    targetImage,
  });

  useEffect(() => {
    if (isFetchingRevertedVersions) return;
    if (revertedVersions.length === 0) return;

    addToReverted();
  }, [isFetchingRevertedVersions, revertedVersions.length]);

  const versionsForImage = mergedVersions
    .filter(version => version.meta?.merged_from?.toString() === image.id.toString());
  const imageModels = versionImageModels.filter(imageModel => imageModel.image_id === image.id);

  const imageIds = watch('imageIds');

  const url = new URL(window.location.href);
  url.searchParams.set('id[eq]', image.id.toString());

  // if there's no previous model and no image models referencing the current image,
  // then the card hasn't been reverted and there's no existing image model to revert.
  if (!previousImageModel && imageModels.length === 0 && !isFetchingVersionImageModels) return null;
  if (isFetchingVersionImageModels || isFetchingMergedVersions || isFetchingRevertedVersions) return null;

  const isReverted = imageModels.length > 0;
  const numUses = isReverted ? imageModels.length : previouslyUsed.length;

  return (
    <div
      className={cn(
        'tw-flex tw-items-center tw-gap-3 tw-py-2 tw-px-3 tw-w-full tw-box-border',
        'tw-border-b tw-border-solid tw-border-med-grey last-of-type:tw-border-none',
      )}
    >
      {!isReverted ? (
        <input
          checked={imageIds.includes(image.id.toString())}
          className="tw-cursor-pointer tw-m-0"
          type="checkbox"
          value={image.id}
          {...register('imageIds')}
        />
      ) : (
        <Icon
          className="tw-bg-light-grey tw-font-dark-grey tw-p-0.5 tw-rounded tw-w-5 tw-h-4 tw-box-border"
          icon="History"
          size={12}
        />
      )}
      <img alt={image.image_en_file_name} className="tw-max-w-12" src={image.url} />
      <div className="tw-w-full">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-0.5">
          <Text className="!tw-leading-none" size="sm" weight="bold">
            {image.image_en_file_name}
          </Text>
          {!isReverted ? (
            <div
              className={cn(
                'tw-inline-flex tw-bg-green tw-bg-opacity-10 tw-text-green tw-py-1 tw-px-3 tw-justify-self-end',
                'tw-box-border tw-rounded-full tw-items-center tw-justify-center tw-gap-1 tw-font-bold',
              )}
            >
              <Icon icon="Check" size={12} /> <Text size="xs" tag="span">Merged</Text>
            </div>
          ) : (
            <div
              className={cn(
                'tw-inline-flex tw-bg-dark-grey tw-bg-opacity-10 tw-text-very-dark-grey',
                'tw-py-1 tw-px-3 tw-justify-self-end',
                'tw-box-border tw-rounded-full tw-items-center tw-justify-center tw-gap-1 tw-font-bold',
              )}
            >
              <Icon icon="BackTo" size={12} /> <Text size="xs" tag="span">Reverted</Text>
            </div>
          )}
        </div>
        <div className="tw-flex tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-1">
            <Text className="!tw-leading-none !tw-text-dark-grey" size="sm" variant="label" weight="light">
              {`ID: ${image.id}`}
            </Text>
            <Text asChild size="xs">
              <div
                className={cn(
                  'tw-text-very-dark-grey tw-bg-light-grey tw-border tw-border-solid',
                  'tw-border-med-grey tw-rounded tw-h-4 tw-px-1 tw-flex tw-items-center tw-justify-center',
                )}
              >
                {numUses} {!isReverted && 'previous'} {pluralize('use', numUses)}
              </div>
            </Text>
            <a
              className="tw-flex tw-items-center tw-gap-1 tw-ml-2"
              href={url.href}
              rel="noopener noreferrer"
              target="_blank"
            >
              View Merge History <Icon icon="ExternalLink" size={16} />
            </a>
          </div>
          {versionsForImage[0] && revertedVersions.length === 0 && (
            <Text
              className="!tw-text-dark-grey tw-ml-auto !tw-leading-none tw-place-content-end"
              size="xs"
              variant="body"
            >
              {moment(versionsForImage[0].created_at).tz(moment.tz.guess()).format('M/D/YY h:mm A z')}
            </Text>
          )}
          {revertedVersions.length > 0 && (
            <Text
              className="!tw-text-dark-grey tw-ml-auto !tw-leading-none tw-place-content-end"
              size="xs"
              variant="body"
            >
              {moment(revertedVersions[0].created_at).tz('America/Los Angeles').format('M/D/YY h:mm A z')}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};
